import type { Rect } from "../scene/geometry";
import type { ClipFraming } from "../scene/schema";
import { videoContentInsets, videoOrientation } from "./frame";

/**
 * Placement of a clip's picture in the video frame. The editor preview (CSS)
 * and the export (ffmpeg crop/scale/overlay) both use these numbers, so the
 * framing looks the same in both.
 */

export interface Size {
  width: number;
  height: number;
}

/** Rectangle of the full source picture in frame coordinates (may exceed the frame). */
export function placeClip(frame: Size, source: Size, framing: ClipFraming): Rect {
  const sw = Math.max(1, source.width);
  const sh = Math.max(1, source.height);
  const base =
    framing.fit === "cover"
      ? Math.max(frame.width / sw, frame.height / sh)
      : Math.min(frame.width / sw, frame.height / sh);
  const scale = base * framing.zoom;
  const width = sw * scale;
  const height = sh * scale;
  return {
    x: ((frame.width - width) / 2) * (1 + framing.x),
    y: ((frame.height - height) / 2) * (1 + framing.y),
    width,
    height,
  };
}

/** Rectangle covering the whole frame with the source (for blurred backgrounds). */
export function coverRect(frame: Size, source: Size): Rect {
  return placeClip(frame, source, { fit: "cover", zoom: 1, x: 0, y: 0, flipX: false });
}

export interface VisibleRegion {
  /** Part of the source in source pixels. */
  source: Rect;
  /** Where it lands in the frame, in frame pixels. */
  target: Rect;
}

/**
 * The visible part of a placed picture: what to crop from the source and where
 * it goes. Null when nothing is visible. Values are rounded to even pixels
 * (needed for yuv420p in ffmpeg).
 */
export function visibleRegion(
  frame: Size,
  source: Size,
  placed: Rect,
  flipX = false,
): VisibleRegion | null {
  const left = Math.max(0, placed.x);
  const top = Math.max(0, placed.y);
  const right = Math.min(frame.width, placed.x + placed.width);
  const bottom = Math.min(frame.height, placed.y + placed.height);
  if (right - left < 2 || bottom - top < 2) return null;
  const sx = source.width / placed.width;
  const sy = source.height / placed.height;
  const even = (value: number) => Math.max(2, Math.round(value / 2) * 2);
  let srcX = (left - placed.x) * sx;
  const srcWidth = (right - left) * sx;
  // A mirrored picture shows the opposite side of the source.
  if (flipX) srcX = source.width - srcX - srcWidth;
  const src = {
    x: Math.max(0, Math.floor(srcX)),
    y: Math.max(0, Math.floor((top - placed.y) * sy)),
    width: even(Math.min(source.width, srcWidth)),
    height: even(Math.min(source.height, (bottom - top) * sy)),
  };
  src.width = Math.min(src.width, Math.floor((source.width - src.x) / 2) * 2);
  src.height = Math.min(src.height, Math.floor((source.height - src.y) / 2) * 2);
  return {
    source: src,
    target: {
      x: Math.round(left),
      y: Math.round(top),
      width: even(right - left),
      height: even(bottom - top),
    },
  };
}

// ---------------------------------------------------------------------------
// Overlay clips (video tracks above the main track)
// ---------------------------------------------------------------------------

/** Rectangle of the full source picture of an overlay clip, in frame coordinates. */
export function placeOverlay(box: Rect, source: Size, framing: ClipFraming): Rect {
  const placed = placeClip(box, source, framing);
  return { ...placed, x: box.x + placed.x, y: box.y + placed.y };
}

export const OVERLAY_LAYOUTS = [
  "full",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "center",
  "top-half",
  "bottom-half",
  "left-half",
  "right-half",
] as const;
export type OverlayLayout = (typeof OVERLAY_LAYOUTS)[number];

export const OVERLAY_LAYOUT_LABELS: Record<OverlayLayout, string> = {
  full: "Vollbild",
  "top-left": "Oben links",
  "top-right": "Oben rechts",
  "bottom-left": "Unten links",
  "bottom-right": "Unten rechts",
  center: "Mitte",
  "top-half": "Obere Hälfte",
  "bottom-half": "Untere Hälfte",
  "left-half": "Linke Hälfte",
  "right-half": "Rechte Hälfte",
};

/** The layouts offered for a format: halves side by side in landscape, on top of each other otherwise. */
export function overlayLayoutsForFrame(frame: Size): OverlayLayout[] {
  const landscape = videoOrientation(frame) === "landscape";
  return OVERLAY_LAYOUTS.filter((layout) =>
    landscape
      ? layout !== "top-half" && layout !== "bottom-half"
      : layout !== "left-half" && layout !== "right-half",
  );
}

/**
 * Box of a layout preset: full frame, picture in picture in a corner (the
 * source's aspect ratio, 42 % of the frame width) or half of the frame for
 * split screens.
 */
export function overlayLayoutBox(layout: OverlayLayout, frame: Size, source: Size): Rect {
  const W = frame.width;
  const H = frame.height;
  if (layout === "full") return { x: 0, y: 0, width: W, height: H };
  if (layout === "top-half") return { x: 0, y: 0, width: W, height: Math.round(H / 2) };
  if (layout === "bottom-half")
    return { x: 0, y: Math.round(H / 2), width: W, height: H - Math.round(H / 2) };
  if (layout === "left-half") return { x: 0, y: 0, width: Math.round(W / 2), height: H };
  if (layout === "right-half")
    return { x: Math.round(W / 2), y: 0, width: W - Math.round(W / 2), height: H };
  const aspect = Math.max(0.2, Math.min(5, source.width / Math.max(1, source.height)));
  let width = W * (layout === "center" ? 0.7 : 0.42);
  let height = width / aspect;
  if (height > H * 0.45) {
    height = H * 0.45;
    width = height * aspect;
  }
  const margin = Math.round(Math.min(W, H) * 0.05);
  width = Math.round(width);
  height = Math.round(height);
  // Below the status bar and above the app's caption area (see the safe zones).
  const insets = videoContentInsets(frame);
  const top = insets.top;
  const bottom = H - insets.bottom - height;
  switch (layout) {
    case "top-left":
      return { x: margin, y: top, width, height };
    case "top-right":
      return { x: W - margin - width, y: top, width, height };
    case "bottom-left":
      return { x: margin, y: bottom, width, height };
    case "bottom-right":
      return { x: W - margin - width, y: bottom, width, height };
    default:
      return { x: Math.round((W - width) / 2), y: Math.round((H - height) / 2), width, height };
  }
}
